/**
 * Shared Pulse types used by frontend and backend plugins.
 *
 * @packageDocumentation
 */

import type { CheckFilter } from './checks';
import type { DefinitionSource } from './definition-source';

/**
 * Stable integration and collector identifiers shared across Pulse packages.
 */
export enum PulseIntegrationId {
  CATALOG = 'catalog',
  UNPROCESSED = 'unprocessed',
  HTTP = 'http',
}

/**
 * Transport-safe schedule shape shared between Pulse backend responses and
 * frontend consumers.
 *
 * This is intentionally a DTO contract for data transfer and not the backend
 * runtime schedule type from `@backstage/backend-plugin-api`.
 */
export type PulseCollectorScheduleDto = {
  frequency?: { cron: string };
  timeout?: unknown;
  initialDelay?: unknown;
  scope?: unknown;
};

/**
 * Opaque transport-safe collector configuration map.
 *
 * Values remain untyped in this shared package because each collector defines
 * and validates its own runtime configuration schema.
 */
export type PulseCollectorConfig = Record<string, unknown>;

/**
 * Integration list entry used by API responses and UI list rendering.
 */
export type PulseIntegrationListItem = {
  integrationId: string;
  title?: string;
  description?: string;
};

/**
 * Full fact collector descriptor including schedule and collector config.
 */
export type PulseFactCollectorInfo = {
  integrationId: string;
  factCollectorId: string;
  /** Group that owns the collector; gates conditional update/delete. */
  ownerEntityRef?: string;
  /** `config` collectors come from app-config YAML and are read-only in the UI. */
  source?: DefinitionSource;
  schedule: PulseCollectorScheduleDto;
  collectorConfig: PulseCollectorConfig;
};

/**
 * UI-managed HTTP fact collector, as sent to the create/update endpoints.
 */
export type HttpFactCollectorDefinition = {
  factName: string;
  ownerEntityRef: string;
  entityFilter?: CheckFilter[];
  targetPluginId?: string;
  request: {
    url: string;
    headers?: Record<string, string>;
  };
  response?: {
    emptyOnStatuses?: number[];
    transform?: string;
  };
  schedule: {
    frequency: { cron: string };
  };
};

/**
 * Check definition and result types shared between backend and frontend.
 * These types describe the structure of Pulse checks that are evaluated
 * against collected facts.
 */
export * from './checks';

/**
 * Origin of a Pulse definition (`config` YAML or UI-managed `db`).
 */
export * from './definition-source';

/**
 * Path resolution and value matching used to evaluate check filter rules.
 */
export * from './entity-filter';

/**
 * Track definition and result types shared between backend and frontend.
 * Tracks organize checks into ordered levels and certify entities against them.
 */
export * from './tracks';
export * from './track-schemas';

/**
 * Aggregated, owner-scoped track compliance contract served by the insights
 * routes and consumed by the insights table and the home page widget.
 */
export * from './insights';

/**
 * Pulse permission definitions and resource type constants, shared between
 * the frontend and backend plugins.
 */
export {
  parsePulseCheckResourceRef,
  PULSE_CHECK_RESOURCE_TYPE,
  PULSE_CHECK_RESULT_RESOURCE_TYPE,
  PULSE_CHECK_EXEMPTION_RESOURCE_TYPE,
  PULSE_CHECK_DEFINITION_RESOURCE_TYPE,
  PULSE_FACT_COLLECTOR_DEFINITION_RESOURCE_TYPE,
  PULSE_RESOURCE_REF_SEPARATOR,
  toPulseCheckResourceRef,
  toPulseCheckDefinitionResourceRef,
  toPulseFactCollectorDefinitionResourceRef,
  pulseAdminPermission,
  pulseCheckReadPermission,
  pulseCheckRefreshPermission,
  pulseCheckManualUpdatePermission,
  pulseCheckExemptionUpdatePermission,
  pulseCheckCreatePermission,
  pulseCheckUpdatePermission,
  pulseCheckDeletePermission,
  pulseFactCollectorReadPermission,
  pulseFactCollectorCreatePermission,
  pulseFactCollectorUpdatePermission,
  pulseFactCollectorDeletePermission,
  pulseFactSensitiveReadPermission,
  pulseFactSensitiveOwnerReadPermission,
  pulseTrackReadPermission,
  pulseTrackCreatePermission,
  pulseTrackUpdatePermission,
  pulseTrackDeletePermission,
  pulseTrackRecertifyPermission,
  PULSE_TRACK_DEFINITION_RESOURCE_TYPE,
  toPulseTrackDefinitionResourceRef,
  pulsePermissions,
} from './permissions';

/**
 * Pulse frontend helpers
 */
export * from './helpers';
